import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MeetingDetail, MinistryMeetings, PersonRef } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { fromLocalInput, toLocalInput } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

const OUTSIDE = '__fuera__';

function PersonSelect({ id, label, name, people, defaultValue, allowOutside = false, onChange }: {
  id: string; label: string; name: string; people: PersonRef[]; defaultValue?: string; allowOutside?: boolean; onChange?: (v: string) => void;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <SearchSelect
        id={id}
        name={name}
        defaultValue={defaultValue ?? ''}
        onChange={onChange}
        emptyLabel={allowOutside ? 'Sin definir' : 'Nadie todavía'}
        options={[...(allowOutside ? [{ value: OUTSIDE, label: 'Alguien de fuera (escribir nombre)' }] : []), ...personOptions(people)]}
      />
    </div>
  );
}

/** Crear (desde un ministerio) o editar una reunión. */
export function MeetingFormPage() {
  const { ministryId, meetingId } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      if (meetingId) {
        const meeting = await api.get<MeetingDetail>(`/organization/churches/${churchId}/meetings/${meetingId}`, signal);
        return { meeting, options: null };
      }
      const options = await api.get<MinistryMeetings>(`/organization/churches/${churchId}/ministries/${ministryId}/meetings`, signal);
      return { meeting: null, options };
    },
    [churchId, ministryId, meetingId],
  );
  const [params] = useSearchParams();
  const back = meetingId ? `/app/reuniones/${meetingId}` : params.get('desde') === 'reuniones' ? '/app/reuniones' : `/app/ministerios/${ministryId}`;

  return (
    <div className="page">
      <p className="small">
        <Link to={back}>← Volver</Link>
      </p>
      <h1>{meetingId ? 'Editar reunión' : 'Nueva reunión'}</h1>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && data.data && churchId ? <MeetingForm churchId={churchId} ministryId={ministryId ?? ''} cellId={params.get('celula')} {...data.data} /> : null}
    </div>
  );
}

function MeetingForm({ churchId, ministryId, cellId, meeting, options }: { churchId: string; ministryId: string; cellId: string | null; meeting: MeetingDetail | null; options: MinistryMeetings | null }) {
  const navigate = useNavigate();
  const people = meeting ? meeting.candidates : (options?.candidates ?? []);
  const targets = options ? [...(options.canCreateForMinistry ? [{ id: '', name: 'Todo el ministerio' }] : []), ...options.cellsICanManage.map((c) => ({ id: c.id, name: `Célula ${c.name}` }))] : [];
  const [speaker, setSpeaker] = useState(meeting?.speaker ? (meeting.speaker.personId ?? OUTSIDE) : '');
  const [hasFood, setHasFood] = useState(meeting?.hasFood ?? false);
  const [isPublic, setIsPublic] = useState(meeting?.isPublic ?? false);
  const [food, setFood] = useState<{ item: string; personId: string }[]>([]);
  // Desde Reuniones ya viene elegida la célula (o todo el ministerio).
  const [target, setTarget] = useState(cellId && targets.some((t) => t.id === cellId) ? cellId : (targets[0]?.id ?? ''));
  const [combined, setCombined] = useState<string[]>(meeting?.withMinistries.map((x) => x.id) ?? []);
  const ministryOptions = meeting ? meeting.ministryOptions : (options?.ministries ?? []);
  const canCombine = (meeting ? !meeting.cell : target === '') && ministryOptions.length > 0;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!meeting && targets.length === 0) return <EmptyState title="Solo el líder o co-líder del ministerio, o el representante de una célula, crean reuniones." />;

  const start = new Date(Date.now() + 3 * 86400_000);
  start.setHours(19, 30, 0, 0);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const t = (k: string) => String(form.get(k) ?? '').trim() || null;
    const starts = String(form.get('startsAt') ?? '');
    const ends = String(form.get('endsAt') ?? '');
    if (!t('topic')) return setError('Escribe el tema de la reunión.');
    if (!starts || !ends) return setError('Indica cuándo empieza y termina.');
    const body = {
      topic: t('topic'),
      speakerId: speaker && speaker !== OUTSIDE ? speaker : null,
      speakerName: speaker === OUTSIDE ? t('speakerName') : null,
      hostId: t('hostId'),
      startsAt: fromLocalInput(starts),
      endsAt: fromLocalInput(ends),
      placeName: t('placeName'),
      address: t('address'),
      mapsUrl: t('mapsUrl'),
      hasFood,
      foodNotes: hasFood ? t('foodNotes') : null,
      notes: t('notes'),
      isPublic,
      withMinistryIds: canCombine ? combined : [],
    };
    setBusy(true);
    setError(null);
    try {
      if (meeting) {
        await api.put(`/organization/churches/${churchId}/meetings/${meeting.id}`, body);
        navigate(`/app/reuniones/${meeting.id}`);
      } else {
        const created = await api.post<{ id: string }>(`/organization/churches/${churchId}/ministries/${ministryId}/meetings`, {
          ...body,
          cellId: t('target'),
          food: hasFood ? food.filter((f) => f.item.trim()).map((f) => ({ item: f.item.trim(), personId: f.personId || null })) : [],
        });
        navigate(`/app/reuniones/${created.id}`);
      }
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form className="form event-form" onSubmit={onSubmit} noValidate>
      {error ? <Alert>{error}</Alert> : null}
      <Card title="La reunión">
        {meeting ? (
          <p>
            {meeting.ministry.name} · {meeting.cell ? `Célula ${meeting.cell.name}` : 'Todo el ministerio'}
          </p>
        ) : (
          <div className="field">
            <label htmlFor="mt-target">¿Para quién es?</label>
            <select id="mt-target" name="target" value={target} onChange={(e) => setTarget(e.target.value)}>
              {targets.map((x) => (
                <option key={x.id || 'all'} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {canCombine ? (
          <fieldset className="picker">
            <legend>Combinar con otros ministerios (opcional)</legend>
            <p className="small muted">Sus integrantes también participan y sus líderes la pueden organizar.</p>
            <ul className="picker__list picker__list--grid">
              {ministryOptions.map((x) => (
                <li key={x.id}>
                  <label className="check" htmlFor={`comb-${x.id}`}>
                    <input
                      id={`comb-${x.id}`}
                      type="checkbox"
                      checked={combined.includes(x.id)}
                      onChange={() => setCombined((v) => (v.includes(x.id) ? v.filter((y) => y !== x.id) : [...v, x.id]))}
                    />
                    {x.name}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        ) : null}
        <Field label="Tema" name="topic" required maxLength={150} defaultValue={meeting?.topic} placeholder="El fruto del Espíritu" />
        <div className="form__row">
          <PersonSelect id="mt-speaker" label="¿Quién da el tema?" name="speaker" people={people} allowOutside defaultValue={speaker} onChange={setSpeaker} />
          {speaker === OUTSIDE ? (
            <Field label="Nombre de quien da el tema" name="speakerName" maxLength={120} defaultValue={meeting?.speaker && !meeting.speaker.personId ? meeting.speaker.name : ''} />
          ) : null}
        </div>
        <PersonSelect id="mt-host" label="Encargado" name="hostId" people={people} defaultValue={meeting?.host?.personId ?? ''} />
        <div className="form__row">
          <Field label="Empieza" name="startsAt" type="datetime-local" required defaultValue={toLocalInput(meeting ? new Date(meeting.startsAt) : start)} />
          <Field label="Termina" name="endsAt" type="datetime-local" required defaultValue={toLocalInput(meeting ? new Date(meeting.endsAt) : new Date(start.getTime() + 2 * 3600_000))} />
        </div>
      </Card>

      <Card title="Ubicación">
        <Field label="Lugar" name="placeName" maxLength={200} defaultValue={meeting?.placeName ?? ''} placeholder="Casa de la familia Rivera" />
        <Field label="Dirección" name="address" maxLength={300} defaultValue={meeting?.address ?? ''} />
        <Field label="Ubicación en el mapa" name="mapsUrl" type="url" maxLength={500} defaultValue={meeting?.mapsUrl ?? ''} placeholder="https://maps.app.goo.gl/…" />
        <label className="check">
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Pública: sale en la página de la iglesia y cualquiera la ve, aunque no tenga cuenta
        </label>
      </Card>

      <Card title="Comida">
        <label className="check">
          <input type="checkbox" checked={hasFood} onChange={(e) => setHasFood(e.target.checked)} /> Habrá comida
        </label>
        {hasFood ? (
          <>
            <Field label="¿Qué habrá?" name="foodNotes" maxLength={500} defaultValue={meeting?.foodNotes ?? ''} placeholder="Tacos, pozole, cena de traje…" />
            {meeting ? (
              <p className="small muted">Quién lleva qué se organiza en la página de la reunión.</p>
            ) : (
              <>
                <p className="small">¿Quién lleva qué? (quien no tenga a nadie, cualquiera se puede apuntar)</p>
                {food.map((f, i) => (
                  <div key={i} className="form__row food-row">
                    <Field label={`Cosa ${i + 1}`} value={f.item} maxLength={120} onChange={(e) => setFood((all) => all.map((x, j) => (j === i ? { ...x, item: e.target.value } : x)))} />
                    <div className="field">
                      <label htmlFor={`food-p-${i}`}>La lleva</label>
                      <SearchSelect
                        id={`food-p-${i}`}
                        value={f.personId}
                        onChange={(v) => setFood((all) => all.map((x, j) => (j === i ? { ...x, personId: v } : x)))}
                        emptyLabel="Nadie todavía"
                        options={personOptions(people)}
                      />
                    </div>
                  </div>
                ))}
                <Button type="button" variant="secondary" onClick={() => setFood((all) => [...all, { item: '', personId: '' }])}>
                  Agregar algo que llevar
                </Button>
              </>
            )}
          </>
        ) : null}
      </Card>

      <Card title="Notas">
        <div className="field">
          <label htmlFor="mt-notes">Notas para los asistentes (opcional)</label>
          <textarea id="mt-notes" name="notes" maxLength={2000} defaultValue={meeting?.notes ?? ''} />
        </div>
      </Card>

      <div className="actions">
        <Button type="submit" busy={busy}>
          {meeting ? 'Guardar cambios' : 'Crear reunión'}
        </Button>
      </div>
    </form>
  );
}
